import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import {
  associateLiveRecording,
  getLiveGeneratedVersion,
  getLiveRecordings,
  searchRecordings,
  RecordingCandidate,
  VodRecording
} from '@utils/api/realtime/live-vod.ts';
import { liveUtteranceText } from '@utils/editors/live-transcript-state.ts';
import { formatTime } from '@utils/audio.ts';
import { LiveRecordingPreview } from './live-recording-preview.tsx';

type Props = {
  sessionId: string;
  canEdit: boolean;
  refresh: () => Promise<boolean>;
};
export const LiveRecordings = ({ sessionId, canEdit, refresh }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [open, setOpen] = useState(false);
  const [recordings, setRecordings] = useState<VodRecording[]>([]);
  const [choices, setChoices] = useState<RecordingCandidate[]>([]);
  const [query, setQuery] = useState('');
  const [recording, setRecording] = useState('');
  const [offset, setOffset] = useState('0');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [review, setReview] = useState<string | null>(null);
  const generation = useRef(0);
  const writing = useRef(false);
  useEffect(() => {
    const active = ++generation.current;
    if (!open) return;
    const load = async () => {
      try {
        const values = await getLiveRecordings(sessionId);
        if (active === generation.current) {
          setRecordings(values);
          setError(false);
          await refresh();
        }
      } catch {
        if (active === generation.current) setError(true);
      }
    };
    void load();
    const timer = window.setInterval(() => {
      if (!writing.current) void load();
    }, 10000);
    return () => {
      ++generation.current;
      window.clearInterval(timer);
    };
  }, [open, sessionId, refresh]);
  useEffect(() => {
    if (!open || !canEdit) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void searchRecordings(query, controller.signal)
        .then(values => {
          if (!controller.signal.aborted)
            setChoices(
              values.filter(
                item =>
                  item.session_id !== sessionId && item.status !== 'streaming'
              )
            );
        })
        .catch(() => {
          if (!controller.signal.aborted) setError(true);
        });
    }, 250);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [open, query, sessionId, canEdit]);
  const offsetMs = Math.round(Number(offset) * 1000);
  const valid =
    offset.trim() !== '' &&
    Number.isFinite(offsetMs) &&
    Math.abs(offsetMs) <= 43200000;
  const run = async (work: () => Promise<void>) => {
    if (writing.current) return;
    const active = generation.current;
    writing.current = true;
    setBusy(true);
    setError(false);
    try {
      await work();
    } catch {
      if (active === generation.current) setError(true);
    } finally {
      writing.current = false;
      if (active === generation.current) setBusy(false);
    }
  };
  const attach = (event: FormEvent) => {
    event.preventDefault();
    if (!canEdit || !recording || !valid) return;
    void run(async () => {
      const active = generation.current;
      const values = await associateLiveRecording(
        sessionId,
        recording,
        offsetMs
      );
      if (active !== generation.current) return;
      setRecordings(values);
      await refresh();
    });
  };
  const retry = () =>
    void run(async () => {
      const active = generation.current;
      let values = recordings;
      if (canEdit) {
        for (const item of recordings) {
          values = await associateLiveRecording(
            sessionId,
            item.recording_session_id,
            item.offset_ms
          );
          if (active !== generation.current) return;
        }
      } else values = await getLiveRecordings(sessionId);
      if (active !== generation.current) return;
      setRecordings(values);
      await refresh();
    });
  const inspect = (version: string) =>
    void run(async () => {
      const active = generation.current;
      const body = await getLiveGeneratedVersion(sessionId, version);
      if (active === generation.current)
        setReview(
          body.document.document
            .map(
              row =>
                `${row.speaker || t('speaker')}${typeof row.start === 'number' && typeof row.stop === 'number' ? ` [${formatTime(row.start / 1000)}–${formatTime(row.stop / 1000)}]` : ''}:\n${liveUtteranceText(row)}`
            )
            .join('\n\n')
        );
    });
  const fieldStyle = {
    backgroundColor: getColour('baseLayer'),
    color: getColour('normalText'),
    borderColor: getColour('border')
  };
  return (
    <section
      className="rounded-xl border p-3"
      style={{
        borderColor: getColour('border'),
        backgroundColor: getColour('secondLayer')
      }}
    >
      <Button
        variant="outline"
        size="small"
        label={t('live-recordings')}
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        disabled={busy}
      />
      {open && (
        <div className="flex flex-col gap-3 mt-3 max-h-[40vh] overflow-auto">
          <p className="text-sm">{t('live-vod-rule')}</p>
          {canEdit && (
            <form onSubmit={attach} className="flex flex-wrap gap-2 items-end">
              <label className="flex flex-col gap-1 flex-1 min-w-40 text-sm">
                {t('live-search-recording')}
                <input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  className="border rounded-lg p-2"
                  style={fieldStyle}
                />
              </label>
              <label className="flex flex-col gap-1 flex-1 min-w-40 text-sm">
                {t('live-recording')}
                <select
                  value={recording}
                  onChange={event => setRecording(event.target.value)}
                  className="border rounded-lg p-2"
                  style={fieldStyle}
                >
                  <option value="">{t('live-choose-recording')}</option>
                  {choices.map(item => (
                    <option key={item.session_id} value={item.session_id}>
                      {item.filename || t('live-recording')}
                      {item.date_session_start &&
                        ` · ${new Date(item.date_session_start).toLocaleString()}`}{' '}
                      · {item.status}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                {t('live-recording-offset')}
                <input
                  type="number"
                  min="-43200"
                  max="43200"
                  step="0.001"
                  value={offset}
                  onChange={event => setOffset(event.target.value)}
                  className="border rounded-lg p-2 w-32"
                  style={fieldStyle}
                />
              </label>
              <Button
                type="submit"
                size="small"
                label={t('live-attach-recording')}
                disabled={busy || !recording || !valid}
              />
              <p className="text-xs w-full">
                {t('live-recording-offset-help')}
              </p>
            </form>
          )}
          {error && <p role="alert">{t('live-recording-error')}</p>}
          <Button
            variant="outline"
            size="small"
            label={t('live-check-recordings')}
            disabled={busy}
            onClick={retry}
          />
          {recordings.map(item => (
            <article
              key={item.recording_session_id}
              className="border rounded-lg p-3"
              style={{ borderColor: getColour('border') }}
            >
              <h2 className="font-medium">
                {item.filename || t('live-recording')}
              </h2>
              {item.pending && (
                <p role="status" className="text-sm">
                  {t('live-recording-pending')}
                </p>
              )}
              {item.versions.map(version => (
                <div
                  key={version.version_id}
                  className="flex flex-wrap gap-2 items-center mt-2"
                >
                  <p className="text-sm flex-1">
                    {t('live-recording-summary', {
                      applied: version.applied_segments,
                      protected: version.protected_segments,
                      unmatched:
                        version.unmatched_segments +
                        version.unmatched_recording_segments
                    })}
                  </p>
                  <Button
                    variant="outline"
                    size="small"
                    label={t('live-review-generated')}
                    disabled={busy}
                    onClick={() => inspect(version.version_id)}
                  />
                </div>
              ))}
            </article>
          ))}
          {review !== null && (
            <LiveRecordingPreview text={review} close={() => setReview(null)} />
          )}
        </div>
      )}
    </section>
  );
};
